import * as React from 'react'
import { AlertTriangle, Building2, Check, ClipboardList, Copy, RefreshCw } from 'lucide-react'
import type { CustomerAssessmentStatus } from '@acs/shared'
import { ApiError } from '@/lib/api'
import { cn } from '@/lib/cn'
import {
  assessmentWhenLabel,
  buildAssessmentMessage,
  messageLines,
  sendableQuestions,
  whatsappDigits,
  whatsappHref,
} from '@/lib/assessment-message'
import { useCustomerAssessment, usePrefetchAssessment } from '@/hooks/use-assessment'
import { Badge, type Tone } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { CopyButton, writeClipboard } from '@/components/ui/copy-button'
import { Dialog } from '@/components/ui/dialog'
import { EmptyState } from '@/components/ui/empty-state'
import { Skeleton } from '@/components/ui/skeleton'
import { useToast } from '@/components/ui/toast'
import { Tooltip } from '@/components/ui/tooltip'

/* ------------------------------------------------------------------ *
 * Bits
 * ------------------------------------------------------------------ */

/** Copy confirmation, matched to `CopyButton` so the two do not blink apart. */
const COPIED_MS = 1_600

const STATUS_TONE: Record<CustomerAssessmentStatus, Tone> = {
  completed: 'ok',
  running: 'info',
  stopped: 'warn',
  failed: 'danger',
}

/**
 * WhatsApp's green, darkened until white text clears WCAG AA on it (5.0:1).
 * The one place in the console a button is a solid brand colour: it is a link
 * OUT to another product, and the colour says which one before the label does.
 */
const WHATSAPP_BUTTON =
  '!border-transparent !bg-[#12803f] !text-white hover:!bg-[#0f6e36] active:!bg-[#0c5c2d]'

/** The WhatsApp glyph — lucide carries no brand icons, so it is drawn inline. */
export function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={className}>
      <path d="M17.5 14.4c-.3-.1-1.8-.9-2-1-.3-.1-.5-.1-.7.1-.2.3-.8 1-.9 1.2-.2.2-.3.2-.6.1-.3-.1-1.3-.5-2.4-1.5-.9-.8-1.5-1.8-1.7-2.1-.2-.3 0-.5.1-.6l.4-.5c.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5l-.9-2.2c-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.5s1.1 2.9 1.2 3.1c.1.2 2.1 3.2 5.1 4.5.7.3 1.3.5 1.7.6.7.2 1.4.2 1.9.1.6-.1 1.8-.7 2-1.4.2-.7.2-1.3.2-1.4-.1-.2-.3-.3-.6-.4zM12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2zm0 18.3a8.3 8.3 0 0 1-4.2-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.3 8.3 0 1 1 12 20.3z" />
    </svg>
  )
}

/* ------------------------------------------------------------------ *
 * The drawer's entry point
 * ------------------------------------------------------------------ */

/**
 * A wide, self-describing action rather than another icon in a row: it says
 * what it opens and what happens next. Rendered only when the caller has
 * established the assessment is done. Hover and focus both warm the cache, so
 * the dialog usually opens already populated.
 */
export function AssessmentCard(p: { customerId: string; onOpen: () => void }) {
  const prefetch = usePrefetchAssessment()
  const intent = () => prefetch(p.customerId)

  return (
    <button
      type="button"
      onClick={p.onOpen}
      onPointerEnter={intent}
      onFocus={intent}
      className={cn(
        'group flex w-full items-center gap-3 rounded-lg border border-ok/30 bg-ok-soft px-3.5 py-3 text-left',
        'transition-colors duration-100 hover:border-ok/60 hover:bg-ok/15',
        'max-md:py-3.5'
      )}
    >
      <span
        aria-hidden="true"
        className="grid size-9 flex-none place-items-center rounded-md bg-ok/15 text-ok"
      >
        <ClipboardList className="size-[18px]" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[13px] leading-[1.35] font-semibold text-fg">
          Assessment questions
        </span>
        <span className="mt-0.5 block truncate text-[11.5px] leading-[1.45] text-muted">
          Completed · review the message and send it on WhatsApp
        </span>
      </span>
      <span
        aria-hidden="true"
        className="grid size-7 flex-none place-items-center rounded-full bg-ok/15 text-ok transition-transform duration-100 group-hover:translate-x-0.5"
      >
        <WhatsAppIcon className="size-4" />
      </span>
    </button>
  )
}

/* ------------------------------------------------------------------ *
 * The dialog
 * ------------------------------------------------------------------ */

/**
 * The customer's assessment questions, as the message that hands them back —
 * QUESTIONS ONLY, by decision: the record holds no spoken answers and this
 * shows none. The work-history employer sits above the message, outside it.
 *
 * ONE view, the message preview: the exact text that will be sent is the whole
 * point, and it, the copy and the WhatsApp link all come from one pure function
 * over the same data, so what the operator reads is what goes out. The preview
 * resolves WhatsApp's `*bold*` the way the customer's phone will, so it is
 * also the readable list of questions.
 *
 * Data is read only while open, and prefetched from the card, so the drawer
 * never pays for a record most opens do not look at.
 */
export function AssessmentDialog(p: {
  customerId: string
  customerName: string
  phone: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const query = useCustomerAssessment(p.customerId, p.open)
  const { toast } = useToast()

  const record = query.data ?? null
  const message = React.useMemo(
    () => (record ? buildAssessmentMessage(p.customerName, record) : ''),
    [record, p.customerName]
  )
  const questionCount = React.useMemo(
    () => (record ? sendableQuestions(record.questions).length : 0),
    [record]
  )
  const digits = whatsappDigits(p.phone)
  const waHref = digits !== null && message !== '' ? whatsappHref(digits, message) : null

  /* -------- copy, with a confirmation that only claims what landed ---- */
  const [copied, setCopied] = React.useState(false)
  const copyTimer = React.useRef<number | null>(null)
  const clearCopyTimer = React.useCallback(() => {
    if (copyTimer.current !== null) {
      window.clearTimeout(copyTimer.current)
      copyTimer.current = null
    }
  }, [])

  React.useEffect(() => clearCopyTimer, [clearCopyTimer])

  // A tick from the last open must not survive into the next one.
  React.useEffect(() => {
    if (!p.open) {
      clearCopyTimer()
      setCopied(false)
    }
  }, [p.open, clearCopyTimer])

  const copy = React.useCallback(async () => {
    if (message === '') return
    const ok = await writeClipboard(message)
    if (!ok) {
      toast({
        tone: 'danger',
        title: 'Could not copy the message',
        description: 'Select the message text and copy it by hand.',
      })
      return
    }
    setCopied(true)
    clearCopyTimer()
    copyTimer.current = window.setTimeout(() => {
      setCopied(false)
      copyTimer.current = null
    }, COPIED_MS)
  }, [message, toast, clearCopyTimer])

  // The same label the message body carries, so header and message agree.
  const when = record ? assessmentWhenLabel(record.endedAt ?? record.startedAt) : null
  // Narrowed once, here, so every branch below reads one value: a record with
  // something to send, or nothing.
  const shown = record !== null && questionCount > 0 ? record : null

  return (
    <Dialog
      open={p.open}
      onOpenChange={p.onOpenChange}
      widthClass="w-[min(640px,calc(100vw-1.5rem))]"
      title="Assessment questions"
      description={
        <span className="flex min-w-0 items-center gap-2">
          <span className="truncate">{p.customerName || p.customerId}</span>
          {when ? (
            <>
              <span aria-hidden="true" className="text-faint">
                ·
              </span>
              <span className="tabular truncate">{when}</span>
            </>
          ) : null}
        </span>
      }
      footer={
        shown !== null ? (
          <>
            <Button
              variant="secondary"
              size="md"
              onClick={() => void copy()}
              className={cn(copied && 'border-ok/40 bg-ok-soft text-ok')}
            >
              {copied ? <Check aria-hidden="true" strokeWidth={3} /> : <Copy aria-hidden="true" />}
              {copied ? 'Copied' : 'Copy message'}
            </Button>

            {/* The footer's phone rules target direct <button> children; the
                link and the tooltip wrapper are not buttons, so they carry the
                same thumb-sized shape themselves. */}
            {waHref !== null ? (
              <Button
                variant="primary"
                size="md"
                asChild
                className={cn(WHATSAPP_BUTTON, 'max-sm:h-11 max-sm:w-full max-sm:text-[14px]')}
              >
                <a href={waHref} target="_blank" rel="noopener noreferrer">
                  <WhatsAppIcon />
                  Send on WhatsApp
                </a>
              </Button>
            ) : (
              <Tooltip
                content={
                  p.phone
                    ? `“${p.phone}” is not a number WhatsApp can open. Copy the message instead.`
                    : 'No phone number on file — copy the message instead.'
                }
              >
                {/* A disabled button is not a hover target; the span is what
                    the tooltip can attach to. */}
                <span className="inline-flex max-sm:w-full">
                  <Button
                    variant="primary"
                    size="md"
                    disabled
                    className="w-full max-sm:h-11 max-sm:text-[14px]"
                  >
                    <WhatsAppIcon />
                    Send on WhatsApp
                  </Button>
                </span>
              </Tooltip>
            )}
          </>
        ) : undefined
      }
    >
      {/* Mounted for the life of the dialog so the announcement lands in a
          region assistive technology is already watching. */}
      <span role="status" aria-live="polite" className="sr-only">
        {copied ? 'Message copied to clipboard' : ''}
      </span>

      {query.isPending ? (
        <PreviewSkeleton />
      ) : query.isError && query.data === undefined ? (
        /* Only a read that never succeeded is an error screen. A refetch that
           fails behind content already on screen keeps the content — the
           questions did not stop being true because one request did. */
        <EmptyState
          icon={<AlertTriangle />}
          title="Could not load the assessment"
          description={
            query.error instanceof ApiError ? query.error.message : 'The request failed.'
          }
          action={
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void query.refetch()}
              loading={query.isFetching}
            >
              <RefreshCw aria-hidden="true" />
              Retry
            </Button>
          }
        />
      ) : shown === null ? (
        <EmptyState
          icon={<ClipboardList />}
          title="No questions recorded"
          description={
            record?.status
              ? `The assessment is marked ${record.status}, but no spoken questions were captured for it.`
              : 'This customer has no assessment on file.'
          }
        />
      ) : (
        <div className="space-y-3 py-1">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            {shown.status ? (
              <Badge tone={STATUS_TONE[shown.status]} dot>
                {shown.status}
              </Badge>
            ) : null}
            <span className="text-[12.5px] text-muted">
              <span className="tabular font-semibold text-fg">{questionCount}</span>
              {questionCount === 1 ? ' question' : ' questions'}
            </span>
            {query.isFetching ? (
              <span className="text-[11.5px] text-faint" aria-live="polite">
                Refreshing…
              </span>
            ) : null}
          </div>

          {shown.workHistoryEmployer ? <EmployerRow name={shown.workHistoryEmployer} /> : null}

          <MessagePreview text={message} />
        </div>
      )}
    </Dialog>
  )
}

/* ------------------------------------------------------------------ *
 * The preview
 * ------------------------------------------------------------------ */

function PreviewSkeleton() {
  return (
    <div className="space-y-3 py-1" aria-busy="true">
      <div className="flex items-center gap-3">
        <Skeleton className="h-5 w-24 rounded-md" />
        <Skeleton className="h-4 w-20" />
      </div>
      <Skeleton className="h-14 w-full rounded-lg" />
      <div className="space-y-2.5 rounded-lg border border-line bg-s1 p-4">
        <Skeleton className="h-3.5 w-1/3" />
        <Skeleton className="h-3.5 w-11/12" />
        <Skeleton className="mt-4 h-3.5 w-1/4" />
        <Skeleton className="h-3.5 w-full" />
        <Skeleton className="h-3.5 w-full" />
        <Skeleton className="h-3.5 w-3/4" />
        <Skeleton className="mt-4 h-3.5 w-1/4" />
        <Skeleton className="h-3.5 w-full" />
        <Skeleton className="h-3.5 w-5/6" />
      </div>
    </div>
  )
}

/**
 * The employer the work history named. It was picked for the customer from a
 * list, so it is something to tell them; copyable, and kept out of the message
 * so the preview stays exactly what is sent.
 */
function EmployerRow({ name }: { name: string }) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-line bg-s1 px-3.5 py-2.5">
      <Building2 aria-hidden="true" className="size-4 flex-none text-faint" />
      <div className="min-w-0 flex-1">
        <div className="text-[11px] font-medium tracking-[0.06em] text-faint uppercase">
          Work history employer
        </div>
        <div className="truncate text-[13px] font-semibold text-fg">{name}</div>
      </div>
      <CopyButton value={name} label="Copy employer" />
    </div>
  )
}

/**
 * The exact text that will be sent, drawn as the chat bubble the customer will
 * see it in. `*bold*` lines render bold the way WhatsApp renders them; the
 * string underneath is untouched, and it is the string that is copied and
 * sent. Selectable, for a by-hand copy where the clipboard is unavailable.
 */
function MessagePreview({ text }: { text: string }) {
  const lines = React.useMemo(() => messageLines(text), [text])

  return (
    <div className="rounded-lg border border-line bg-s1 p-2">
      <div className="flex items-center justify-between gap-3 px-2 pt-1 pb-2">
        <span className="text-[11px] font-medium tracking-[0.06em] text-faint uppercase">
          Message preview
        </span>
        <span className="text-[11px] text-faint">as the customer will see it</span>
      </div>

      <div
        className={cn(
          'relative rounded-md rounded-tl-sm border border-line bg-s2 px-4 py-3.5',
          'text-[13px] leading-[1.6] text-fg selection:bg-accent-soft'
        )}
      >
        {lines.map((line, i) =>
          line.kind === 'blank' ? (
            <div key={i} className="h-[0.85em]" aria-hidden="true" />
          ) : line.kind === 'bold' ? (
            <p key={i} className="font-semibold text-fg">
              {line.text}
            </p>
          ) : (
            <p key={i} className="whitespace-pre-wrap break-words">
              {line.text}
            </p>
          )
        )}
      </div>

      <p className="px-2 pt-2 pb-1 text-[11px] leading-[1.5] text-faint">
        <span className="tabular">{text.length.toLocaleString()}</span> characters · opens
        pre-filled in WhatsApp, you press send.
      </p>
    </div>
  )
}
